import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import {
  APPROVAL_COLOR,
  APPROVAL_LABEL,
  castVote,
  requestApproval,
  saveAssessment,
  saveTicketChange,
  ticketChangeQuery,
  type ChangeForm,
  type TicketChange,
  type Vote,
} from '../api/changeManagement'
import { ApiError } from '../api/client'
import { toast } from '../ui/notify'
import './TicketChangePanel.css'
import { NEW_TAB } from '../ui/NewTab'

/**
 * CEREREA DE SCHIMBARE, pe fisa tichetului (cerinta CEC L27, L30, L31, L32).
 *
 * Se deseneaza NUMAI pe un tichet al carui tip e marcat ca change (Settings →
 * Easeedesk → Ticket types); pe orice alt tichet nu lasa nici macar un chenar.
 *
 * Patru bucati, in ordinea in care le parcurge un change:
 *   1. planul - clasa, planul de implementare, planul de revenire, fereastra,
 *      riscul; elementele afectate sunt cele de pe fila Affected items;
 *   2. ciocnirile - alt change sau o fereastra de mentenanta pe acelasi
 *      element, in acelasi timp;
 *   3. evaluarea riscului - chestionarul furnizorului, completat la cerere;
 *   4. aprobarea CAB - cererea, voturile (care nu se mai schimba) si hotararea.
 */

const EMPTY: ChangeForm = {
  change_class: 2,
  implementation_plan: '',
  backout_plan: '',
  planned_start: '',
  planned_end: '',
  risk_level: 0,
}

function failed(error: unknown) {
  toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Change', 'error')
}

export function TicketChangePanel({ id }: { id: number }) {
  const { data } = useQuery(ticketChangeQuery(id))

  /* Un tichet obisnuit, o eroare, sau inca nimic: niciun chenar. Fisa nu are
     voie sa se clatine fiindca un modul pe care tichetul nu-l foloseste n-a
     raspuns. */
  if (!data || !data.isChange) return null

  return <ChangeBody id={id} page={data} />
}

type ChangePage = Extract<TicketChange, { isChange: true }>

function ChangeBody({ id, page }: { id: number; page: ChangePage }) {
  const queryClient = useQueryClient()
  const change = page.change
  const state = change.approval_state
  const editable = page.canEdit && !page.closed && state !== 'pending'
  const reload = () => queryClient.invalidateQueries({ queryKey: ['easeedesk', 'ticket', id] })

  return (
    <div className="tk-change" data-state={state}>
      <div className="tk-change-head">
        <strong>
          <i className="fas fa-random" aria-hidden="true"></i> Change request
        </strong>
        <span className="tk-change-class">{change.class_label}</span>
        <span className="tk-change-state" style={{ background: APPROVAL_COLOR[state] }}>
          {APPROVAL_LABEL[state]}
        </span>
        {change.risk_label !== '' && (
          <span className={`tk-change-risk tk-change-risk-${change.risk_level}`}>
            {change.risk_label} risk
            {change.risk_score !== null ? ` (${change.risk_score})` : ''}
          </span>
        )}
      </div>

      {page.clashes.length > 0 && (
        <div className="alert alert-warning tk-change-clashes" role="alert">
          <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>{' '}
          <strong>
            {page.clashes.length} clash{page.clashes.length === 1 ? '' : 'es'} in the planned window:
          </strong>
          <ul>
            {page.clashes.map((clash) => (
              <li key={`${clash.kind}-${clash.id}`}>
                {clash.kind === 'change' ? (
                  <a href={`/app/easeedesk/ticket/${clash.id}`} {...NEW_TAB}>
                    change #{clash.id} {clash.name}
                  </a>
                ) : (
                  <a href="/app/maintenance/calendar" {...NEW_TAB}>maintenance window {clash.name}</a>
                )}{' '}
                — {clash.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="row">
        <div className="col-md-7">
          <PlanForm id={id} page={page} editable={editable} onSaved={reload} />
        </div>
        <div className="col-md-5">
          <Approval id={id} page={page} onDone={reload} />
          <Meetings page={page} />
          <Assessment id={id} page={page} editable={editable} onSaved={reload} />
        </div>
      </div>
    </div>
  )
}

function PlanForm({ id, page, editable, onSaved }: { id: number; page: ChangePage; editable: boolean; onSaved(): void }) {
  const change = page.change
  const [form, setForm] = useState<ChangeForm>(EMPTY)

  useEffect(() => {
    setForm({
      change_class: change.change_class,
      implementation_plan: change.implementation_plan,
      backout_plan: change.backout_plan,
      planned_start: change.planned_start,
      planned_end: change.planned_end,
      risk_level: change.risk_level,
    })
  }, [change])

  const save = useMutation({
    mutationFn: () => saveTicketChange(id, form),
    onSuccess: (answer) => {
      toast(answer.message, 'Change', answer.reset ? 'warning' : 'success')
      onSaved()
    },
    onError: failed,
  })

  return (
    <form
      className="tk-change-plan"
      onSubmit={(e) => {
        e.preventDefault()
        if (editable) save.mutate()
      }}
    >
      <div className="row">
        <div className="col-sm-4 form-group">
          <label className="control-label" htmlFor={`chClass${id}`}>
            Class
          </label>
          <select
            id={`chClass${id}`}
            className="form-control input-sm"
            disabled={!editable}
            value={form.change_class}
            onChange={(e) => setForm({ ...form, change_class: Number(e.target.value) })}
          >
            {page.classes.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-sm-4 form-group">
          <label className="control-label" htmlFor={`chStart${id}`}>
            Planned start
          </label>
          <input
            id={`chStart${id}`}
            type="datetime-local"
            className="form-control input-sm"
            disabled={!editable}
            value={form.planned_start}
            onChange={(e) => setForm({ ...form, planned_start: e.target.value })}
          />
        </div>
        <div className="col-sm-4 form-group">
          <label className="control-label" htmlFor={`chEnd${id}`}>
            Planned end
          </label>
          <input
            id={`chEnd${id}`}
            type="datetime-local"
            className="form-control input-sm"
            disabled={!editable}
            value={form.planned_end}
            onChange={(e) => setForm({ ...form, planned_end: e.target.value })}
          />
        </div>
      </div>

      <div className="form-group">
        <label className="control-label" htmlFor={`chImpl${id}`}>
          Implementation plan
        </label>
        <textarea
          id={`chImpl${id}`}
          className="form-control input-sm"
          rows={4}
          disabled={!editable}
          value={form.implementation_plan}
          onChange={(e) => setForm({ ...form, implementation_plan: e.target.value })}
        />
      </div>

      <div className="form-group">
        <label className="control-label" htmlFor={`chBack${id}`}>
          Backout plan
        </label>
        <textarea
          id={`chBack${id}`}
          className="form-control input-sm"
          rows={3}
          disabled={!editable}
          value={form.backout_plan}
          onChange={(e) => setForm({ ...form, backout_plan: e.target.value })}
        />
      </div>

      <div className="row">
        <div className="col-sm-4 form-group">
          <label className="control-label" htmlFor={`chRisk${id}`}>
            Risk
          </label>
          <select
            id={`chRisk${id}`}
            className="form-control input-sm"
            disabled={!editable}
            value={form.risk_level}
            onChange={(e) => setForm({ ...form, risk_level: Number(e.target.value) })}
          >
            <option value={0}>— not assessed —</option>
            {page.risks.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-sm-8 form-group">
          <label className="control-label">Affected items</label>
          <p className="form-control-static tk-change-cis">
            {page.cis.length === 0 ? (
              <span className="color-light-grey">None yet - add them on the Affected items tab.</span>
            ) : (
              page.cis.map((ci) => (
                <span key={ci.id} className="label label-default">
                  {ci.name}
                </span>
              ))
            )}
          </p>
        </div>
      </div>

      {editable && (
        <div className="aright">
          <button type="submit" className="btn btn-primary btn-sm tk-change-save" disabled={save.isPending}>
            Save the plan
          </button>
        </div>
      )}
      {!editable && state(page) === 'pending' && (
        <p className="color-light-grey">The CAB is voting on this plan; it can be edited again once they have decided.</p>
      )}
    </form>
  )
}

function state(page: ChangePage) {
  return page.change.approval_state
}

function Assessment({ id, page, editable, onSaved }: { id: number; page: ChangePage; editable: boolean; onSaved(): void }) {
  const [open, setOpen] = useState(false)
  const [picked, setPicked] = useState<Record<string, number>>({})

  useEffect(() => {
    const start: Record<string, number> = {}
    for (const question of page.questions) {
      const answer = page.answers[String(question.ID)]
      const index = answer ? question.options.findIndex((o) => o.label === answer.answer) : -1
      if (index >= 0) start[String(question.ID)] = index
    }
    setPicked(start)
  }, [page.questions, page.answers])

  const save = useMutation({
    mutationFn: () => saveAssessment(id, picked),
    onSuccess: (answer) => {
      toast(answer.message, 'Change')
      setOpen(false)
      onSaved()
    },
    onError: failed,
  })

  if (page.questions.length === 0) return null
  const complete = page.questions.every((q) => picked[String(q.ID)] !== undefined)

  return (
    <div className="tk-change-box tk-change-assessment">
      <div className="tk-change-box-head">
        <strong>Risk assessment</strong>
        {page.change.risk_source === 'questionnaire' && page.change.risk_score !== null && (
          <span className="color-light-grey">
            {' '}
            — {page.change.risk_score} points, medium from {page.thresholds.medium_from}, high from{' '}
            {page.thresholds.high_from}
          </span>
        )}
        {editable && !open && (
          <button type="button" className="btn btn-default btn-xs pull-right tk-change-assess" onClick={() => setOpen(true)}>
            {page.change.risk_source === 'questionnaire' ? 'Reassess' : 'Assess the risk'}
          </button>
        )}
      </div>

      {open && (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (complete) save.mutate()
          }}
        >
          {page.questions.map((question) => (
            <fieldset key={question.ID} className="tk-change-question">
              <legend>{question.question}</legend>
              {question.options.map((option, index) => (
                <label key={option.label} className="radio-inline">
                  <input
                    type="radio"
                    name={`q${id}-${question.ID}`}
                    checked={picked[String(question.ID)] === index}
                    onChange={() => setPicked({ ...picked, [String(question.ID)]: index })}
                  />{' '}
                  {option.label}
                </label>
              ))}
            </fieldset>
          ))}
          <div className="aright">
            <button type="button" className="btn btn-default btn-xs" onClick={() => setOpen(false)}>
              Cancel
            </button>{' '}
            <button type="submit" className="btn btn-primary btn-xs tk-change-assess-save" disabled={!complete || save.isPending}>
              Save the assessment
            </button>
          </div>
        </form>
      )}
    </div>
  )
}

const VOTE_LABEL: Record<Vote, string> = { approve: 'Approved', reject: 'Rejected', comment: 'Comment' }
const VOTE_ICON: Record<Vote, string> = { approve: 'fa-check', reject: 'fa-times', comment: 'fa-comment' }

function Approval({ id, page, onDone }: { id: number; page: ChangePage; onDone(): void }) {
  const [comment, setComment] = useState('')
  const change = page.change
  const st = change.approval_state

  const ask = useMutation({
    mutationFn: () => requestApproval(id),
    onSuccess: (answer) => {
      toast(answer.message, 'Change')
      onDone()
    },
    onError: failed,
  })

  const vote = useMutation({
    mutationFn: (kind: Vote) => castVote(id, kind, comment),
    onSuccess: (answer) => {
      toast(answer.message, 'Change')
      setComment('')
      onDone()
    },
    onError: failed,
  })

  const canAsk = page.canEdit && !page.closed && (st === 'draft' || st === 'rejected')
  const canDecide = page.isMember && st === 'pending' && page.myVote === null
  const round = change.approval_round
  const votes = page.votes.filter((v) => v.round === round)
  const earlier = page.votes.filter((v) => v.round !== round)

  return (
    <div className="tk-change-box tk-change-approval">
      <div className="tk-change-box-head">
        <strong>CAB approval</strong>
      </div>

      {change.cab ? (
        <p className="tk-change-cab">
          {change.cab.group_name} — {change.cab.rule_label.toLowerCase()}
          {round > 1 ? `, round ${round}` : ''}
        </p>
      ) : page.approvalStep ? (
        <p className="tk-change-cab color-light-grey">
          The step “{page.approvalStep.name}” to {page.approvalStep.to} needs CAB approval.
        </p>
      ) : (
        change.change_class !== 1 && (
          <p className="tk-change-cab color-light-grey">No step leaving the current status asks for CAB approval.</p>
        )
      )}

      {page.tally && st !== 'draft' && (
        <p className="tk-change-tally">
          <span className="text-success">{page.tally.approvals} for</span> ·{' '}
          <span className="text-danger">{page.tally.rejections} against</span> · {page.tally.waiting} still to vote
        </p>
      )}

      {canAsk && (
        <button type="button" className="btn btn-warning btn-sm tk-change-request" disabled={ask.isPending} onClick={() => ask.mutate()}>
          <i className="fas fa-gavel" aria-hidden="true"></i>{' '}
          {change.change_class === 1 ? 'Approve as a standard change' : st === 'rejected' ? 'Request approval again' : 'Request CAB approval'}
        </button>
      )}

      {votes.length > 0 && <VoteList votes={votes} />}
      {earlier.length > 0 && (
        <details className="tk-change-earlier">
          <summary>Earlier rounds ({earlier.length})</summary>
          <VoteList votes={earlier} />
        </details>
      )}

      {page.isMember && change.cab && (
        <div className="tk-change-vote">
          <textarea
            className="form-control input-sm"
            rows={2}
            placeholder={canDecide ? 'Comment (optional for a vote)' : 'Comment'}
            aria-label="Comment for the CAB"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <div className="aright">
            {canDecide && (
              <>
                <button type="button" className="btn btn-success btn-xs tk-change-approve" disabled={vote.isPending} onClick={() => vote.mutate('approve')}>
                  <i className="fas fa-check" aria-hidden="true"></i> Approve
                </button>{' '}
                <button type="button" className="btn btn-danger btn-xs tk-change-reject" disabled={vote.isPending} onClick={() => vote.mutate('reject')}>
                  <i className="fas fa-times" aria-hidden="true"></i> Reject
                </button>{' '}
              </>
            )}
            <button
              type="button"
              className="btn btn-default btn-xs tk-change-comment"
              disabled={vote.isPending || comment.trim() === ''}
              onClick={() => vote.mutate('comment')}
            >
              <i className="fas fa-comment" aria-hidden="true"></i> Comment
            </button>
          </div>
          {page.myVote !== null && st === 'pending' && (
            <p className="color-light-grey">You voted: {VOTE_LABEL[page.myVote].toLowerCase()}. Votes cannot be changed.</p>
          )}
        </div>
      )}
    </div>
  )
}

const MEETING_DECISION: Record<string, string> = { approve: 'approved', reject: 'rejected', defer: 'deferred' }

/** Sedintele CAB care au change-ul pe agenda, cu hotararea fiecareia. */
function Meetings({ page }: { page: ChangePage }) {
  const meetings = page.meetings ?? []
  return (
    <div className="tk-change-meetings">
      <strong>CAB meetings</strong>{' '}
      <a href="/app/easeedesk/cab_meetings" className="color-light-grey">
        all
      </a>
      {meetings.length === 0 ? (
        <div className="color-light-grey">Not on the agenda of any CAB meeting.</div>
      ) : (
        <ul className="list-unstyled">
          {meetings.map((m) => (
            <li key={m.ID}>
              <a href={`/app/easeedesk/cab_meetings/${m.ID}`}>{m.title}</a>{' '}
              <span className="color-light-grey">
                {m.meeting_at_local.replace('T', ' ')}
                {m.status === 'cancelled' ? ' · cancelled' : ''}
                {m.decision !== '' ? ' · ' + (MEETING_DECISION[m.decision] ?? m.decision) : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function VoteList({ votes }: { votes: ChangePage['votes'] }) {
  return (
    <ul className="tk-change-votes">
      {votes.map((v) => (
        <li key={v.ID} className={`tk-change-vote-${v.vote}`}>
          <i className={`fas ${VOTE_ICON[v.vote]}`} aria-hidden="true"></i> <strong>{v.user_name}</strong>{' '}
          <span className="color-light-grey">
            {VOTE_LABEL[v.vote].toLowerCase()} · {v.created_at.slice(0, 16)}
          </span>
          {v.comment !== '' && <div className="tk-change-vote-text">{v.comment}</div>}
        </li>
      ))}
    </ul>
  )
}
